import React, { useState, useEffect } from 'react'
import { storeInfoService, StoreInfo } from '../services/storeInfoService'

interface StoreInfoFormData {
  businessName: string
  street: string
  city: string
  state: string
  zipCode: string
  description: string
  phone: string
  email: string
  website: string
  facebook: string
  instagram: string
  twitter: string
  monday: string
  tuesday: string
  wednesday: string
  thursday: string
  friday: string
  saturday: string
  sunday: string
}

const AdminStoreInfoManager: React.FC = () => {
  const [storeInfo, setStoreInfo] = useState<StoreInfo>(storeInfoService.getStoreInfo())
  const [formData, setFormData] = useState<StoreInfoFormData>({
    businessName: '',
    street: '',
    city: '',
    state: '',
    zipCode: '',
    description: '',
    phone: '',
    email: '',
    website: '',
    facebook: '',
    instagram: '',
    twitter: '',
    monday: '',
    tuesday: '',
    wednesday: '',
    thursday: '',
    friday: '',
    saturday: '',
    sunday: ''
  })
  const [isSaving, setIsSaving] = useState(false)
  const [saveStatus, setSaveStatus] = useState<'idle' | 'success' | 'error'>('idle')

  // Initialize form data from store info
  useEffect(() => {
    const info = storeInfoService.getStoreInfo()
    setFormData({
      businessName: info.businessName,
      street: info.address.street,
      city: info.address.city,
      state: info.address.state,
      zipCode: info.address.zipCode,
      description: info.address.description || '',
      phone: info.contact.phone,
      email: info.contact.email,
      website: info.contact.website,
      facebook: info.social.facebook || '',
      instagram: info.social.instagram || '',
      twitter: info.social.twitter || '',
      monday: info.hours.monday,
      tuesday: info.hours.tuesday,
      wednesday: info.hours.wednesday,
      thursday: info.hours.thursday,
      friday: info.hours.friday,
      saturday: info.hours.saturday,
      sunday: info.hours.sunday
    })
  }, [])

  // Subscribe to store info changes
  useEffect(() => {
    const unsubscribe = storeInfoService.subscribe(() => {
      setStoreInfo(storeInfoService.getStoreInfo())
    })
    return unsubscribe
  }, [])

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: value
    }))
  }

  const handleSave = async () => {
    setIsSaving(true)
    setSaveStatus('idle')

    try {
      const updatedStoreInfo: StoreInfo = {
        businessName: formData.businessName,
        address: {
          street: formData.street,
          city: formData.city,
          state: formData.state,
          zipCode: formData.zipCode,
          description: formData.description || undefined
        },
        contact: {
          phone: formData.phone,
          email: formData.email,
          website: formData.website
        },
        social: {
          facebook: formData.facebook || undefined,
          instagram: formData.instagram || undefined,
          twitter: formData.twitter || undefined
        },
        hours: {
          monday: formData.monday,
          tuesday: formData.tuesday,
          wednesday: formData.wednesday,
          thursday: formData.thursday,
          friday: formData.friday,
          saturday: formData.saturday,
          sunday: formData.sunday
        },
        description: storeInfo.description // Keep existing description
      }

      storeInfoService.updateStoreInfo(updatedStoreInfo)
      setSaveStatus('success')
      
      // Clear success message after 3 seconds
      setTimeout(() => setSaveStatus('idle'), 3000)
    } catch (error) {
      console.error('Failed to save store information:', error)
      setSaveStatus('error')
    } finally {
      setIsSaving(false)
    }
  }

  const handleReset = () => {
    storeInfoService.resetToDefaults()
    // Re-initialize form data
    const info = storeInfoService.getStoreInfo()
    setFormData({
      businessName: info.businessName,
      street: info.address.street,
      city: info.address.city,
      state: info.address.state,
      zipCode: info.address.zipCode,
      description: info.address.description || '',
      phone: info.contact.phone,
      email: info.contact.email,
      website: info.contact.website,
      facebook: info.social.facebook || '',
      instagram: info.social.instagram || '',
      twitter: info.social.twitter || '',
      monday: info.hours.monday,
      tuesday: info.hours.tuesday,
      wednesday: info.hours.wednesday,
      thursday: info.hours.thursday,
      friday: info.hours.friday,
      saturday: info.hours.saturday,
      sunday: info.hours.sunday
    })
    setSaveStatus('idle')
  }

  const handleDownloadJson = () => {
    // Build the StoreInfo object from current form data
    const storeInfoJson: StoreInfo = {
      businessName: formData.businessName,
      address: {
        street: formData.street,
        city: formData.city,
        state: formData.state,
        zipCode: formData.zipCode,
        description: formData.description || undefined
      },
      contact: {
        phone: formData.phone,
        email: formData.email,
        website: formData.website
      },
      social: {
        facebook: formData.facebook || undefined,
        instagram: formData.instagram || undefined,
        twitter: formData.twitter || undefined
      },
      hours: {
        monday: formData.monday,
        tuesday: formData.tuesday,
        wednesday: formData.wednesday,
        thursday: formData.thursday,
        friday: formData.friday,
        saturday: formData.saturday,
        sunday: formData.sunday
      },
      description: storeInfo.description
    }

    // Create a blob from the JSON data
    const jsonString = JSON.stringify(storeInfoJson, null, 2)
    const blob = new Blob([jsonString], { type: 'application/json' })

    // Create download link and trigger download
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = 'store-info.json'
    document.body.appendChild(link)
    link.click()

    // Cleanup
    document.body.removeChild(link)
    URL.revokeObjectURL(url)
  }

  const commonHours = [
    'Closed',
    '9:00 AM - 5:00 PM',
    '10:00 AM - 4:00 PM',
    '10:00 AM - 6:00 PM',
    '11:00 AM - 7:00 PM',
    '12:00 PM - 8:00 PM'
  ]

  return (
    <div className="container-fluid">
      <div className="row">
        <div className="col-12">
          <div className="d-flex justify-content-between align-items-center mb-4">
            <h2 className="h4 mb-0">🏪 Store Information Management</h2>
            <div className="d-flex gap-2">
              <button
                type="button"
                onClick={handleReset}
                className="btn btn-outline-secondary"
                disabled={isSaving}
              >
                Reset to Defaults
              </button>
              <button
                type="button"
                onClick={handleDownloadJson}
                className="btn btn-outline-primary"
                disabled={isSaving}
              >
                📥 Download JSON
              </button>
              <button
                type="button"
                onClick={handleSave}
                className="btn btn-primary"
                disabled={isSaving}
              >
                {isSaving ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                    Saving...
                  </>
                ) : (
                  'Save Changes'
                )}
              </button>
            </div>
          </div>

          <div className="alert alert-info" role="alert">
            <h6 className="alert-heading mb-2">📋 How to Deploy Hours Changes to Production</h6>
            <ol className="mb-0 small">
              <li>Make your changes to the business hours above</li>
              <li>Click "Save Changes" to preview locally</li>
              <li>Click "Download JSON" to download the store-info.json file</li>
              <li>Replace <code>/public/data/store-info.json</code> in your repository with the downloaded file</li>
              <li>Commit and push to git, then deploy to Netlify</li>
              <li>All users will see the updated hours after deployment!</li>
            </ol>
            <p className="mb-0 mt-2 small text-muted">
              <strong>Note:</strong> "Save Changes" only updates your local browser. To make changes visible to all users across all devices, you must download the JSON file and deploy it.
            </p>
          </div>

          {saveStatus === 'success' && (
            <div className="alert alert-success alert-dismissible fade show" role="alert">
              ✅ Store information saved successfully! Changes are now reflected in your browser. Don't forget to download and deploy the JSON for production!
            </div>
          )}

          {saveStatus === 'error' && (
            <div className="alert alert-danger alert-dismissible fade show" role="alert">
              ❌ Failed to save store information. Please try again.
            </div>
          )}

          <div className="row g-4">
            {/* Business Information */}
            <div className="col-lg-6">
              <div className="card">
                <div className="card-header">
                  <h5 className="card-title mb-0">🏢 Business Information</h5>
                </div>
                <div className="card-body">
                  <div className="mb-3">
                    <label htmlFor="businessName" className="form-label">Business Name</label>
                    <input
                      type="text"
                      className="form-control"
                      id="businessName"
                      name="businessName"
                      value={formData.businessName}
                      onChange={handleInputChange}
                      placeholder="The Salty Mermaid Art Studio & Gallery"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Address Information */}
            <div className="col-lg-6">
              <div className="card">
                <div className="card-header">
                  <h5 className="card-title mb-0">📍 Address</h5>
                </div>
                <div className="card-body">
                  <div className="mb-3">
                    <label htmlFor="street" className="form-label">Street Address</label>
                    <input
                      type="text"
                      className="form-control"
                      id="street"
                      name="street"
                      value={formData.street}
                      onChange={handleInputChange}
                      placeholder="1546 Ocean Shore Blvd"
                    />
                  </div>
                  <div className="row">
                    <div className="col-md-6 mb-3">
                      <label htmlFor="city" className="form-label">City</label>
                      <input
                        type="text"
                        className="form-control"
                        id="city"
                        name="city"
                        value={formData.city}
                        onChange={handleInputChange}
                        placeholder="Ormond Beach"
                      />
                    </div>
                    <div className="col-md-3 mb-3">
                      <label htmlFor="state" className="form-label">State</label>
                      <input
                        type="text"
                        className="form-control"
                        id="state"
                        name="state"
                        value={formData.state}
                        onChange={handleInputChange}
                        placeholder="FL"
                        maxLength={2}
                      />
                    </div>
                    <div className="col-md-3 mb-3">
                      <label htmlFor="zipCode" className="form-label">ZIP Code</label>
                      <input
                        type="text"
                        className="form-control"
                        id="zipCode"
                        name="zipCode"
                        value={formData.zipCode}
                        onChange={handleInputChange}
                        placeholder="32176"
                      />
                    </div>
                  </div>
                  <div className="mb-3">
                    <label htmlFor="description" className="form-label">Location Description</label>
                    <input
                      type="text"
                      className="form-control"
                      id="description"
                      name="description"
                      value={formData.description}
                      onChange={handleInputChange}
                      placeholder="Beautiful oceanside location!"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Contact Information */}
            <div className="col-lg-6">
              <div className="card">
                <div className="card-header">
                  <h5 className="card-title mb-0">📞 Contact Information</h5>
                </div>
                <div className="card-body">
                  <div className="mb-3">
                    <label htmlFor="phone" className="form-label">Phone Number</label>
                    <input
                      type="tel"
                      className="form-control"
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      placeholder="(904) 704-7449"
                    />
                  </div>
                  <div className="mb-3">
                    <label htmlFor="email" className="form-label">Email Address</label>
                    <input
                      type="email"
                      className="form-control"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      placeholder="thesaltymermaidart@gmail.com"
                    />
                  </div>
                  <div className="mb-3">
                    <label htmlFor="website" className="form-label">Website</label>
                    <input
                      type="text"
                      className="form-control"
                      id="website"
                      name="website"
                      value={formData.website}
                      onChange={handleInputChange}
                      placeholder="saltymermaidart.com"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Social Media */}
            <div className="col-lg-6">
              <div className="card">
                <div className="card-header">
                  <h5 className="card-title mb-0">🌐 Social Media</h5>
                </div>
                <div className="card-body">
                  <div className="mb-3">
                    <label htmlFor="facebook" className="form-label">Facebook URL</label>
                    <input
                      type="url"
                      className="form-control"
                      id="facebook"
                      name="facebook"
                      value={formData.facebook}
                      onChange={handleInputChange}
                      placeholder="https://www.facebook.com/TheSaltyMermaidArt/"
                    />
                  </div>
                  <div className="mb-3">
                    <label htmlFor="instagram" className="form-label">Instagram URL</label>
                    <input
                      type="url"
                      className="form-control"
                      id="instagram"
                      name="instagram"
                      value={formData.instagram}
                      onChange={handleInputChange}
                      placeholder="https://www.instagram.com/saltymermaidart/"
                    />
                  </div>
                  <div className="mb-3">
                    <label htmlFor="twitter" className="form-label">Twitter URL</label>
                    <input
                      type="url"
                      className="form-control"
                      id="twitter"
                      name="twitter"
                      value={formData.twitter}
                      onChange={handleInputChange}
                      placeholder="https://twitter.com/saltymermaidart"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Business Hours */}
            <div className="col-12">
              <div className="card">
                <div className="card-header">
                  <h5 className="card-title mb-0">🕒 Business Hours</h5>
                </div>
                <div className="card-body">
                  <div className="row">
                    {[
                      { key: 'monday', label: 'Monday' },
                      { key: 'tuesday', label: 'Tuesday' },
                      { key: 'wednesday', label: 'Wednesday' },
                      { key: 'thursday', label: 'Thursday' },
                      { key: 'friday', label: 'Friday' },
                      { key: 'saturday', label: 'Saturday' },
                      { key: 'sunday', label: 'Sunday' }
                    ].map(({ key, label }) => (
                      <div key={key} className="col-md-6 col-lg-4 mb-3">
                        <label htmlFor={key} className="form-label">{label}</label>
                        <select
                          className="form-select"
                          id={key}
                          name={key}
                          value={formData[key as keyof StoreInfoFormData]}
                          onChange={handleInputChange as any}
                        >
                          {commonHours.map(hours => (
                            <option key={hours} value={hours}>{hours}</option>
                          ))}
                          <option value="custom">Custom...</option>
                        </select>
                        {formData[key as keyof StoreInfoFormData] === 'custom' && (
                          <input
                            type="text"
                            className="form-control mt-2"
                            name={key}
                            value={formData[key as keyof StoreInfoFormData]}
                            onChange={handleInputChange}
                            placeholder="Enter custom hours"
                          />
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Preview Section */}
          <div className="row mt-4">
            <div className="col-12">
              <div className="card">
                <div className="card-header">
                  <h5 className="card-title mb-0">👀 Preview</h5>
                </div>
                <div className="card-body">
                  <p className="text-muted">This information will appear on the About page, Contact page, and Footer across your website.</p>
                  <div className="bg-light p-3 rounded">
                    <h6><strong>{formData.businessName}</strong></h6>
                    <p className="mb-1">📍 {formData.street}, {formData.city}, {formData.state} {formData.zipCode}</p>
                    <p className="mb-1">📞 {formData.phone}</p>
                    <p className="mb-1">✉️ {formData.email}</p>
                    <p className="mb-1">🌐 {formData.website}</p>
                    <div className="mt-2">
                      <strong>Hours:</strong>
                      <div className="small">
                        {storeInfoService.getWorkingDaysHours().map((period, index) => (
                          <div key={index}>{period.days}: {period.hours}</div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default AdminStoreInfoManager